import type {
	ColumnConfiguration,
	FieldConfiguration,
	FilterRule,
	ListBuilderConfiguration,
	ListBuilderList,
} from '../../types';

/**
 * PD-17094 — the Projects page (Sales → Projects). Rows come from
 * GET /api/projects (ProjectsService), which applies every filter itself.
 * Each tab's defining predicate is a LOCKED, hidden rule.
 */
const TABLE = 'project';

export const PROJECT_STATUS_VALUE_MAPPING = [
	{ value: 'bidding', label: 'Bidding', color: { type: 'info' } },
	{ value: 'awarded', label: 'Awarded', color: { type: 'success' } },
	{ value: 'in_progress', label: 'In Progress', color: { type: 'warning' } },
	{ value: 'complete', label: 'Complete', color: { type: 'neutral' } },
	{ value: 'canceled', label: 'Canceled', color: { type: 'neutral' } },
];

const text = (
	id: string,
	fieldName: string,
	options: { secondary?: boolean; defaultValue?: string } = {},
): FieldConfiguration => ({
	id,
	dataConfig: {
		type: 'field',
		field: { fieldName, objectTable: TABLE },
		...(options.defaultValue != null ? { defaultValue: options.defaultValue } : {}),
	},
	styleConfig: options.secondary
		? { size: 'sm', colorRule: { type: 'theme', themeColor: 'textSecondary' } }
		: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'text',
	...(options.secondary ? { conditionalVisibility: { field: fieldName, condition: 'is_not_null' } } : {}),
});

const lineBreak = (id: string, field: string): FieldConfiguration => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	styleConfig: { size: 'sm', colorRule: { type: 'default' } },
	displayStyle: 'line_break',
	conditionalVisibility: { field, condition: 'is_not_null' },
});

const column = (
	id: string,
	header: string,
	fields: Array<FieldConfiguration>,
	options: { sortField?: string; preset?: 'small' | 'medium' | 'large'; minWidth?: number; alignment?: 'left' | 'right' } = {},
): ColumnConfiguration => ({
	id,
	fields,
	headers: [{ id: `h-${id}`, text: header }],
	minWidth: options.minWidth ?? 140,
	sortable: options.sortField != null,
	alignment: options.alignment ?? 'left',
	...(options.sortField != null
		? { sortFields: [{ direction: 'ASC' as const, fieldName: options.sortField, objectTable: TABLE }] }
		: {}),
	widthConfig: { mode: 'flexible', preset: options.preset ?? 'medium' },
	verticalAlignment: 'top',
});

export function createDefaultProjectColumns(): Array<ColumnConfiguration> {
	return [
		column(
			'project-name',
			'Project',
			[
				{
					id: 'project-name-field',
					link: { type: 'object', enabled: true, objectTable: TABLE, idField: 'id' },
					dataConfig: { type: 'field', field: { fieldName: 'name', objectTable: TABLE } },
					styleConfig: { size: 'md', weight: 'medium', colorRule: { type: 'default' } },
					displayStyle: 'text',
				},
				lineBreak('project-name-lb', 'siteLabel'),
				text('project-site', 'siteLabel', { secondary: true }),
			],
			{ sortField: 'name', preset: 'large', minWidth: 220 },
		),
		column(
			'project-status',
			'Status',
			[
				{
					id: 'project-status-field',
					dataConfig: { type: 'field', field: { fieldName: 'status', objectTable: TABLE } },
					styleConfig: { size: 'sm', valueMapping: PROJECT_STATUS_VALUE_MAPPING },
					displayStyle: 'badge',
				},
			],
			{ sortField: 'status', preset: 'small', minWidth: 110 },
		),
		column('project-bid', 'Bid Date', [
			{
				id: 'project-bid-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'bidDate', objectTable: TABLE },
					defaultValue: '-',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'date',
			},
		], { sortField: 'bidDate', preset: 'small', minWidth: 110 }),
		column('project-gc', 'General Contractor', [
			text('project-gc-field', 'generalContractorLabel', { defaultValue: '-' }),
		]),
		column('project-owner', 'Building Owner', [
			text('project-owner-field', 'ownerLabel', { defaultValue: '-' }),
		]),
		column('project-involvement', 'Our Customers', [
			text('project-involvement-field', 'involvementLabel', { defaultValue: 'None yet' }),
		], { minWidth: 180 }),
	];
}

const statusRule = (statuses: Array<[string, string]>): FilterRule => ({
	defaultValue: statuses.map(([id, label]) => ({ id, label })),
	locked: true,
	hidden: true,
});

const list = (
	id: string,
	name: string,
	sortOrder: number,
	description: string,
	filterRules: Record<string, FilterRule>,
): ListBuilderList => ({
	id,
	cacheTTL: 600000,
	name,
	type: 'default',
	active: true,
	sortOrder,
	columns: createDefaultProjectColumns(),
	description,
	filterRules,
	defaultSortId: 'bid-desc',
});

export const PROJECT_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		list('default-projects-open', 'Open', 0, 'Projects being bid, awarded or under way', {
			projectStatus: statusRule([
				['bidding', 'Bidding'],
				['awarded', 'Awarded'],
				['in_progress', 'In Progress'],
			]),
		}),
		list('default-projects-bidding', 'Bidding', 1, 'Projects out to bid', {
			projectStatus: statusRule([['bidding', 'Bidding']]),
		}),
		list('default-projects-closed', 'Complete or Canceled', 2, 'Finished and canceled projects', {
			projectStatus: statusRule([
				['complete', 'Complete'],
				['canceled', 'Canceled'],
			]),
		}),
		list('default-projects-all', 'All', 3, 'Every project', {}),
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50],
	},
	filters: {
		activeFilters: ['projectKeywords', 'projectStatus', 'projectBidDate'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/projects/:id',
			objectLabel: 'Project',
			objectTable: TABLE,
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'bidDate', fieldLabel: 'Bid Date' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'generalContractorLabel', fieldLabel: 'General Contractor' },
				{ fieldName: 'ownerLabel', fieldLabel: 'Building Owner' },
				{ fieldName: 'customerCount', fieldLabel: 'Customers' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'bid-desc',
			name: 'Latest Bid Date',
			fields: [{ direction: 'DESC', fieldName: 'bidDate', objectTable: TABLE }],
			description: 'Latest bid date first',
		},
		{
			id: 'name',
			name: 'Name',
			fields: [{ direction: 'ASC', fieldName: 'name', objectTable: TABLE }],
			description: 'Sort projects by name',
		},
		{
			id: 'created-desc',
			name: 'Newest First',
			fields: [{ direction: 'DESC', fieldName: '_created_date', objectTable: TABLE }],
			description: 'Most recently added projects first',
		},
	],
};
